import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { businessAPI, clientAPI } from '../../services/apiServices';
import { Button, Input, Select, Alert, Card, PageHeader, Spinner, Textarea } from '../../components/common/UI';
import DateField from '../../components/common/DateField';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { requiredFieldMessage, requiredFieldsMessage } from '../../utils/validationMessages';
import { toDateInputValue, todayInputValue } from '../../utils/dateUtils';
import styles from './BusinessPages.module.css';
import QuickContactModal from './tabs/QuickContactModal';
import { useAuth } from '../../contexts/AuthContext';
import { getProfileOwnerName } from '../../utils/businessOwner';

const createEmptyForm = () => ({
  name: '',
  startDate: todayInputValue(),
  endDate: '',
  businessType: '',
  description: '',
  clientId: '',
});
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const isValidISODate = (value) => {
  if (!ISO_DATE_RE.test(value)) return false;
  const date = new Date(`${value}T00:00:00`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

const BusinessFormPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(createEmptyForm);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [planLimit, setPlanLimit] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [quickContactOpen, setQuickContactOpen] = useState(false);

  useEffect(() => {
    clientAPI.getAll().then(({ data }) => setClients(data.data || []));
    if (isEditing) {
      businessAPI.getOne(id)
        .then(({ data }) => {
          const b = data.data;
          setForm({
            name: b.name || '',
            startDate: toDateInputValue(b.startDate),
            endDate: toDateInputValue(b.endDate),
            businessType: b.businessType || '',
            description: b.description || '',
            clientId: b.clientId || '',
          });
        })
        .catch(() => setError(t('businessForm.notFound')))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing, t]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFieldErrors((current) => ({ ...current, [e.target.name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    const name = form.name.trim();
    const businessType = form.businessType.trim();
    const description = form.description.trim();

    if (!name) errors.name = requiredFieldMessage(t, t('businessForm.nameLabel'));
    else if (name.length < 2) errors.name = t('businessForm.validation.nameMin');
    if (name.length > 150) errors.name = t('businessForm.validation.nameMax');
    if (!form.startDate) errors.startDate = requiredFieldMessage(t, t('businessForm.startDateLabel'));
    else if (!isValidISODate(form.startDate)) errors.startDate = t('businessForm.validation.dateFormatWeb');
    if (form.endDate && !isValidISODate(form.endDate)) errors.endDate = t('businessForm.validation.dateFormatWeb');
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      errors.endDate = t('businessForm.validation.endBeforeStart');
    }
    if (businessType.length > 100) errors.businessType = t('businessForm.validation.typeMax');
    if (description.length > 5000) errors.description = t('businessForm.validation.descriptionMax');

    setFieldErrors(errors);
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length) {
      const missingLabels = [
        errors.name === requiredFieldMessage(t, t('businessForm.nameLabel')) ? t('businessForm.nameLabel') : '',
        errors.startDate === requiredFieldMessage(t, t('businessForm.startDateLabel')) ? t('businessForm.startDateLabel') : '',
      ].filter(Boolean);
      setError(missingLabels.length ? requiredFieldsMessage(t, missingLabels) : t('businessForm.validation.review'));
      return;
    }

    setSaving(true);
    setError('');
    setPlanLimit(null);
    try {
      const payload = {
        name: form.name.trim(),
        startDate: form.startDate,
        endDate: form.endDate || null,
        businessType: form.businessType.trim() || null,
        description: form.description.trim() || null,
        clientId: form.clientId || null,
      };

      if (isEditing) {
        await businessAPI.update(id, payload);
        navigate(`/businesses/${id}`);
      } else {
        const { data } = await businessAPI.create(payload);
        navigate(`/businesses/${data.data.id}`);
      }
    } catch (err) {
      if (!isEditing && err.response?.data?.code === 'PLAN_LIMIT_REACHED') {
        const details = err.response.data.details || {};
        setPlanLimit({
          plan: details.plan || 'basic',
          limit: details.businessLimit,
        });
        setError('');
        return;
      }
      setError(getApiErrorMessage(err, t('businessForm.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const handleQuickContactCreated = (contact) => {
    setClients((current) => [contact, ...current.filter((item) => item.id !== contact.id)]);
    setForm((current) => ({ ...current, clientId: contact.id }));
  };

  if (loading) return <Spinner size="lg" />;

  const clientOptions = [
    { value: '', label: getProfileOwnerName(user, t) },
    ...clients.map((contact) => ({ value: contact.id, label: contact.name })),
  ];

  return (
    <div>
      <PageHeader
        title={isEditing ? t('businessForm.editTitle') : t('businessForm.newTitle')}
        subtitle={isEditing ? t('businessForm.editSubtitle') : t('businessForm.newSubtitle')}
      />

      <Card className={styles.formCard}>
        {error && <Alert variant="error" onClose={() => setError('')} style={{ marginBottom: '1rem' }}>{error}</Alert>}
        {planLimit ? (
          <div className={styles.planLimit}>
            <Alert variant="warning">
              {planLimit.plan === 'standard'
                ? t('plans.businessLimitStandardMessage', { limit: planLimit.limit || 40 })
                : t('plans.businessLimitBasicMessage', { limit: planLimit.limit || 3 })}
            </Alert>
            <Link to="/subscription">
              <Button type="button">
                {planLimit.plan === 'standard' ? t('plans.manageSubscription') : t('plans.upgradeToStandard')}
              </Button>
            </Link>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} noValidate>
          <div className={styles.formGrid}>
            <Input
              label={t('businessForm.nameLabel')}
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder={t('businessForm.namePlaceholder')}
              error={fieldErrors.name}
              required
              maxLength={150}
            />
            <Input
              label={t('businessForm.typeLabel')}
              name="businessType"
              value={form.businessType}
              onChange={handleChange}
              placeholder={t('businessForm.typePlaceholder')}
              error={fieldErrors.businessType}
              maxLength={100}
              helpText={t('businessForm.typeHelp')}
            />
            <Textarea
              className={styles.fullWidthField}
              label={t('businessForm.descriptionLabel')}
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder={t('businessForm.descriptionPlaceholder')}
              error={fieldErrors.description}
              maxLength={5000}
              rows={5}
            />
            <DateField
              label={t('businessForm.startDateLabel')}
              name="startDate"
              value={form.startDate}
              onChange={(startDate) => {
                setForm((current) => ({ ...current, startDate }));
                setFieldErrors((current) => ({ ...current, startDate: '' }));
              }}
              error={fieldErrors.startDate}
              helpText={t('businessForm.startDateHelpMobile')}
              required
            />
            <DateField
              label={t('businessForm.endDateLabel')}
              name="endDate"
              value={form.endDate}
              onChange={(endDate) => {
                setForm((current) => ({ ...current, endDate }));
                setFieldErrors((current) => ({ ...current, endDate: '' }));
              }}
              min={form.startDate}
              allowClear
              error={fieldErrors.endDate}
              helpText={t('businessForm.endDateHelp')}
            />
            <div className={styles.contactField}>
              <div className={styles.contactFieldHeader}>
                <span className={styles.contactFieldLabel}>{t('businessForm.responsibleContact')}</span>
                <button className={styles.inlineAddButton} onClick={() => setQuickContactOpen(true)} type="button">
                  <span aria-hidden="true">+</span>
                  {t('quickContact.shortAction')}
                </button>
              </div>
              <Select
                name="clientId"
                value={form.clientId}
                onChange={handleChange}
                options={clientOptions}
              />
            </div>
          </div>

          <div className={styles.formActions}>
            <Button type="submit" loading={saving}>
              {isEditing ? t('profile.saveChanges') : t('businesses.create')}
            </Button>
            <Link to={isEditing ? `/businesses/${id}` : '/businesses'}>
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
          </div>
        </form>
      </Card>
      <QuickContactModal
        isOpen={quickContactOpen}
        onClose={() => setQuickContactOpen(false)}
        onCreated={handleQuickContactCreated}
        clientType="business"
      />
    </div>
  );
};

export default BusinessFormPage;
